import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DiscountSelector } from "@/components/Sales/DiscountSelector";
import { useCustomerProductHistory } from "@/hooks/useCustomerProductHistory";
import { CustomerProductHistoryWithClient } from "@/components/Sales/CustomerProductHistoryWithClient";
import { usePriceTables } from "@/hooks/usePriceTables";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/utils/formatters";
import { Minus, Plus, ShoppingCart, Package, Tag } from "lucide-react";
import { useState, useEffect } from "react";
import type { ProductPrice } from "@/types/priceTable";

interface ProductSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: {
    id: string;
    name: string;
    sku?: string;
    sale_price: number;
    stock_quantity?: number;
    images?: Array<{ image_url: string }>;
  } | null;
  customerId?: string;
  onConfirm: (data: {
    quantity: number;
    unit_price: number;
    discount_percentage: number;
    discount_value: number;
    discount_type: 'percentage' | 'fixed' | null; // NOVO: Tipo de desconto escolhido
    notes?: string;
  }) => void;
}

export function ProductSelectionModal({
  open,
  onOpenChange,
  product,
  customerId,
  onConfirm,
}: ProductSelectionModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed' | null>(null);
  const [notes, setNotes] = useState("");
  const [selectedPriceTableId, setSelectedPriceTableId] = useState<string>("");
  const [productPrices, setProductPrices] = useState<ProductPrice[]>([]);

  const { data: history, isLoading: loadingHistory } = useCustomerProductHistory(
    customerId,
    product?.id
  );

  const { priceTables, fetchProductPrices } = usePriceTables();

  // Carregar preços do produto nas tabelas quando modal abrir
  useEffect(() => {
    if (product?.id && open) {
      fetchProductPrices(product.id).then(setProductPrices).catch(() => setProductPrices([]));
    }
  }, [product?.id, open]);

  // Resetar valores ao abrir modal
  useEffect(() => {
    if (product && open) {
      setUnitPrice(product.sale_price);
      setQuantity(1);
      setDiscountPercentage(0);
      setDiscountValue(0);
      setDiscountType(null);
      setNotes("");
      setSelectedPriceTableId("");
    }
  }, [product, open]);

  // Priorizar preço histórico do cliente quando disponível
  useEffect(() => {
    if (history?.last_unit_price && !selectedPriceTableId && open) {
      setUnitPrice(history.last_unit_price);
    }
  }, [history, open]);

  // Atualizar preço quando selecionar tabela
  const handlePriceTableChange = (tableId: string) => {
    setSelectedPriceTableId(tableId);
    
    if (!tableId || tableId === "default") {
      setUnitPrice(product?.sale_price || 0);
      return;
    }
    
    const priceEntry = productPrices.find(p => p.price_table_id === tableId);
    if (priceEntry) {
      setUnitPrice(priceEntry.sale_table_price);
    } else {
      // Se não tem preço configurado para esta tabela, mantém o preço padrão
      setUnitPrice(product?.sale_price || 0);
    }
  };

  const subtotal = quantity * unitPrice;
  // discountValue é POR UNIDADE - multiplicar por quantidade para obter o total
  const discountAmount = discountValue > 0 
    ? discountValue * quantity 
    : (subtotal * discountPercentage) / 100;
  const total = subtotal - discountAmount;

  const handleConfirm = () => {
    // discountValue é POR UNIDADE - retornar assim para consumidores
    // discount_type preserva a intenção original do usuário
    onConfirm({
      quantity,
      unit_price: unitPrice,
      discount_percentage: discountPercentage,
      discount_value: discountValue, // VALOR POR UNIDADE
      discount_type: discountType, // NOVO: Tipo de desconto escolhido
      notes: notes || undefined,
    });
    onOpenChange(false);
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto pb-32">
        <DialogHeader>
          <div className="flex gap-4">
            {product.images?.[0] && (
              <img
                src={product.images[0].image_url}
                alt={product.name}
                className="w-20 h-20 object-cover rounded-lg border"
              />
            )}
            <div>
              <DialogTitle className="text-xl">{product.name}</DialogTitle>
              {product.sku && (
                <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
              )}
            </div>
          </div>
        </DialogHeader>

        {customerId && (
          <CustomerProductHistoryWithClient
            loading={loadingHistory}
            history={history}
            onLastSaleClick={
              history?.last_sale_number
                ? () =>
                    window.open(
                      `/dashboard/sales?search=${encodeURIComponent(history.last_sale_number!)}`,
                      "_blank",
                    )
                : undefined
            }
          />
        )}

        {/* Seletor de Tabela de Preços */}
        {priceTables.length > 0 && (
          <div className="border rounded-lg p-4 bg-primary/5">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm">Tabela de Preços</h3>
            </div>
            <Select value={selectedPriceTableId} onValueChange={handlePriceTableChange}>
              <SelectTrigger>
                <SelectValue placeholder="Preço padrão do produto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  Preço padrão ({formatCurrency(product.sale_price)})
                </SelectItem>
                {priceTables.map(table => {
                  const priceEntry = productPrices.find(p => p.price_table_id === table.id);
                  return (
                    <SelectItem key={table.id} value={table.id}>
                      {table.name} {priceEntry ? `(${formatCurrency(priceEntry.sale_table_price)})` : '(não configurado)'}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Detalhes do Item */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Detalhes do Item</h3>
          </div>

          {/* Quantidade */}
          <div>
            <label className="text-sm font-medium mb-2 block">Quantidade</label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setQuantity(Math.max(0.001, quantity - 1))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                inputMode="decimal"
                step="0.001"
                min="0.001"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(0.001, parseFloat(e.target.value) || 0.001))}
                className="text-center w-24"
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setQuantity(quantity + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Preço Unitário */}
          <div>
            <label className="text-sm font-medium mb-2 block">Preço Unitário</label>
            <Input
              type="text"
              value={formatCurrency(unitPrice)}
              disabled
              className="bg-muted"
            />
          </div>

          {/* Desconto */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Desconto <span className="text-muted-foreground font-normal">(por unidade)</span>
            </label>
            <DiscountSelector
              subtotal={subtotal}
              discountPercentage={discountPercentage}
              discountValue={discountValue}
              onDiscountChange={(percentage, value) => {
                setDiscountPercentage(percentage);
                setDiscountValue(value);
              }}
              onDiscountTypeChange={(type) => setDiscountType(type)}
              initialDiscountType={discountType}
              quantity={quantity}
              compact
            />
          </div>

          {/* Totais */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span className="font-medium">{formatCurrency(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-red-600">
                <span>Desconto ({discountPercentage > 0 ? `${discountPercentage.toFixed(2)}%` : ''}):</span>
                <span className="font-medium">-{formatCurrency(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold border-t pt-2">
              <span>Total:</span>
              <span className="text-primary">{formatCurrency(total)}</span>
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="text-sm font-medium mb-2 block">Observações</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione observações sobre este item (opcional)"
              rows={3}
              onFocus={(e) => {
                setTimeout(() => {
                  e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
            />
          </div>

        {/* Estoque */}
          {product.stock_quantity !== undefined && (
            <div className="flex items-center gap-2 text-sm">
              <Package className="h-4 w-4" />
              <span className="text-muted-foreground">
                Estoque disponível:{" "}
                <span className={cn(
                  "font-medium",
                  product.stock_quantity < 0 ? "text-red-600" : 
                  product.stock_quantity < quantity ? "text-orange-600" : "text-green-600"
                )}>
                  {product.stock_quantity} unidades
                  {product.stock_quantity < 0 && " (negativo)"}
                </span>
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm}>
            Adicionar Produto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
